import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Loader2, Music } from "lucide-react";
import { Badge } from "../components/ui/badge";
import BeatPlayer from "../components/BeatPlayer";
import ShareButton from "../components/ShareButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLLECTION_META = {
  "late-night-drive": { title: "Late Night Drive", desc: "Dark, moody beats for midnight sessions", gradient: "from-purple-900 to-zinc-900" },
  "studio-heat": { title: "Studio Heat", desc: "Hard-hitting beats ready for bars", gradient: "from-red-900 to-orange-900" },
  "summer-vibes": { title: "Summer Vibes", desc: "Feel-good bouncy beats", gradient: "from-amber-900 to-pink-900" },
  "in-my-feels": { title: "In My Feels", desc: "Emotional, introspective instrumentals", gradient: "from-blue-900 to-indigo-900" },
  "trap-house": { title: "Trap House", desc: "Pure trap bangers", gradient: "from-zinc-900 to-red-900" },
  "cloud-9": { title: "Cloud 9", desc: "Float away with dreamy beats", gradient: "from-violet-900 to-sky-900" },
};

const CollectionBeatsPage = () => {
  const { collectionId } = useParams();
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = COLLECTION_META[collectionId] || { title: collectionId, desc: "", gradient: "from-zinc-900 to-zinc-800" };

  useEffect(() => {
    const fetchBeats = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/discover/curated/${collectionId}`);
        setBeats(res.data.beats || []);
      } catch (e) {
        console.error("Error fetching collection:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchBeats();
  }, [collectionId]);

  return (
    <div className="min-h-screen py-10 px-6" data-testid="collection-beats-page">
      <div className="max-w-7xl mx-auto">
        <Link to="/discover" className="inline-flex items-center gap-2 text-zinc-500 hover:text-neon-cyan mb-6 transition-colors" data-testid="back-to-discover">
          <ArrowLeft className="w-4 h-4" /> Back to Discover
        </Link>

        <div className={`mb-10 p-8 bg-gradient-to-br ${meta.gradient} border border-white/10`}>
          <div className="flex items-center justify-between mb-2">
            <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tight">{meta.title}</h1>
            <ShareButton type="collection" id={collectionId} title={`${meta.title} - The Gas Station`} description={meta.desc} />
          </div>
          <p className="text-zinc-400">{meta.desc}</p>
          <Badge variant="outline" className="border-white/20 mt-3">{beats.length} beats</Badge>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-neon-cyan animate-spin" /></div>
        ) : beats.length === 0 ? (
          <div className="text-center py-20">
            <Music className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No beats in this collection</p>
          </div>
        ) : (
          <div className="space-y-4">
            {beats.map((beat, index) => (
              <motion.div key={beat.beat_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}>
                <BeatPlayer beat={beat} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionBeatsPage;
